import { AgGridReact } from 'ag-grid-react';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { RowNode } from 'ag-grid-enterprise';
import { DocumentWarehousing } from '../../services/http/documentWarehousing.api';
import styles from './exportLabelComponent.module.css';
import mainStyles from '../main.module.css';
import { useExportLabelApi } from '../../hooks/api/exportLabel.hook';
import BaseTable from '../../components/BaseTable';
import LoadingOverlay from '../../components/LoadingOverlay';
import Formatter from '../../utils/formatters';
import { useMessageModal } from '../../hooks/modal.hook';
import { ApiError } from '../../services/http';
import routes from '../../utils/routes';
import FileUtils from '../../utils/file.utils';
import AGUtils from '../../utils/ag-grid.utils';
import ControlCode from '../../utils/controlCode';
import useEffectOnce from '../../hooks/useEffectOnce.hook';
import { useCreateLogApi } from '../../hooks/api/log.hook';
import { LogControlName, LogFormName } from '../../utils/log.utils';
import RadioGroupInput from '../../components/RadioGroupInput';
import SelectInput from '../../components/SelectInput';
import BaseText from '../../components/BaseText/BaseText';
import { STATUS_MESSAGES } from '../../utils/messages';
import { useGetAllTagsApi } from '../../hooks/api/tag.hook';

/**
 * モーダルドキュメントファイルサイズコンポーネント
 *
 * @param props - data - 文書倉庫データ
 */
export function ModalDocumentFileSize(props: { data: DocumentWarehousing }) {
  const { data } = props;

  const fileSize = data.documentFileSize;
  if (fileSize == null) {
    return <div />;
  }

  return <div>{Formatter.byteSizeDescription(fileSize)}</div>;
}

/**
 * ナンバーセル コンポーネント
 *
 * @param props - props.rowIndex - 行インデックス番号
 */
export function NoCell(props: { rowIndex: number }) {
  const { rowIndex } = props;
  return <span>{ rowIndex + 1 }</span>;
}

/**
 * ラベル発行コンポーネントのプロップスインターフェース
 */
export interface ExportLabelProps {
  /** 文書倉庫データ */
  data: DocumentWarehousing[] | null
  /** モーダル表示フラグ */
  display: boolean
  /** モーダル表示フラグをセットする関数 */
  setModalDisplay: (display:boolean) => void
  /** テーブル名 */
  tableName: string,
  /** メイン画面に戻るかどうか */
  returnToMain?: boolean
  /** エクスポート元 */
  exportFrom?: string
}

enum ExportLabelTypes {
  ONE_LABEL = '1',
  TWO_LABELS = '2',
  TEN_LABELS = '10',
}

/**
 * ラベル発行コンポーネント
 */
function ExportLabelComponent({
  data, display, setModalDisplay, tableName, returnToMain, exportFrom,
}: ExportLabelProps) {
  const { allTags, reload: getAllTags, loadingAllTags } = useGetAllTagsApi();
  const { loading: exportLabelloading, request: exportLabelApiRequest } = useExportLabelApi();
  const { request: createLog } = useCreateLogApi();

  const openMessageModal = useMessageModal();
  const navigate = useNavigate();

  const [labelPrintType, setLabelPrintType] = useState<ExportLabelTypes>(ExportLabelTypes.ONE_LABEL);
  const [printStartPosition, setPrintStartPosition] = useState(1);
  const [noRowText, setNoRowText] = useState<string>(STATUS_MESSAGES.NO_DATA);

  const gridRef = useRef<AgGridReact<DocumentWarehousing>>(null);

  const onClose = useCallback(() => {
    if (returnToMain) {
      navigate(routes.main);
    }
    setModalDisplay(false);
  }, [setModalDisplay]);

  const colDefModal = useMemo((): ColDef<DocumentWarehousing>[] => {
    const cols: ColDef<DocumentWarehousing>[] = [
      AGUtils.colAutoIncrement('no', 'No', 0),
      AGUtils.colDefault('ccControlNumber', '文書ID'),
      {
        valueGetter: (params) => (exportFrom === ControlCode.Receiving ? ControlCode.describe(ControlCode.Issuing) : params?.node?.data?.itemCode),
        field: 'itemCode',
        headerName: '保管品バーコード',
        resizable: true,
        sortable: true,
        width: 160,
      },
      AGUtils.colDefault('documentName', '文書名', 250),
      AGUtils.colDefault('documentTypeName', '文書種類', 130),
      AGUtils.colDefault('documentFileName', 'PDFファイル名', 170),
      AGUtils.colFileSize('documentFileSize', 'サイズ'),
      AGUtils.colUsername('registUser', '登録者'),
      AGUtils.colDate('registDate', '登録日時'),
      AGUtils.colUsername('updateUser', '更新者'),
      AGUtils.colDate('updateDate', '更新日時'),
      AGUtils.colDefault('barcodePrinting', 'バーコード印字欄', 150),
    ];

    const tags = new Set<string>(allTags.map((tag) => tag.tagLabel));
    const unique = Array.from(tags);

    unique.forEach((tag) => {
      const apiTag = allTags.find(({ tagLabel }) => (tagLabel === tag)) ?? null;
      cols.push({
        field: `${tag}`,
        headerName: tag,
        resizable: true,
        sortable: true,
        width: 160,
        valueGetter: (valueGetterParams) => {
          const { data: doc } = valueGetterParams;
          const tagValueGetter = AGUtils.getTagValueGetter(tag, apiTag);
          const tagList = doc?.tagList?.map(({ tagLabel, tagValue }) => ({
            label: tagLabel,
            value: tagValue,
          }));
          const parsedData = { tagList };
          return tagValueGetter({ ...valueGetterParams, data: parsedData } as ValueGetterParams);
        },
        comparator: AGUtils.customTagComparator(apiTag),
      });
    });

    return cols;
  }, [allTags]);

  const onExportClick = useCallback(async () => {
    const list: { documentId: number }[] = [];
    gridRef.current?.api?.forEachNodeAfterFilterAndSort((node) => {
      const documentId = node.data?.documentId;
      if (documentId != null) {
        list.push({ documentId });
      }
    });
    try {
      await exportLabelApiRequest({
        documentIdList: list,
        labelPrintType: Number(labelPrintType),
        printStartPosition,
      }).then((d) => {
        const b = FileUtils.fromBase64(`${d.documentLabelFile}`, '保管品ラベル', { type: 'application/pdf' });
        const url = window.URL.createObjectURL(b);
        const a = document.createElement('a');
        a.setAttribute('target', '_blank');
        a.href = url;
        a.click();

        setTimeout(() => {
          const downloadTag = document.createElement('a');
          downloadTag.setAttribute('download', '保管品ラベル');
          downloadTag.href = url;
          downloadTag.click();
          window.URL.revokeObjectURL(url);
        }, 500);

        if (returnToMain) {
          navigate(routes.main);
        }
      });
    } catch (e) {
      await openMessageModal((e as ApiError)?.message);
    }
  }, [exportLabelApiRequest, navigate, openMessageModal, returnToMain, labelPrintType, printStartPosition]);

  const labelDropdownOptions = Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    text: String(i + 1),
  }));

  useEffectOnce(() => {
    createLog(LogFormName.ExportLabel, LogControlName.Show);
  });

  useEffect(() => {
    getAllTags();
    if (data === null) {
      setNoRowText(STATUS_MESSAGES.LOADING_DATA);
    } else if (data.length === 0) {
      setNoRowText(STATUS_MESSAGES.NO_DATA);
    }
  }, [data, getAllTags]);

  if (!display) {
    return (<div />);
  }
  return (
    <div className={styles.modalContainer} role="presentation">
      <div className={styles.modal} role="presentation" onClick={(e) => e.stopPropagation()}>
        <div className={styles.labelTypeContainer}>
          <BaseText bold className={mainStyles['mb-10']}> 保管品ラベル発行タイプ </BaseText>
          <div className={styles.labelTypeRadioContainer}>
            <div className={styles.radioContainer}>
              <RadioGroupInput className={mainStyles['mb-10']} value={labelPrintType} onChange={(value) => setLabelPrintType(value)}>
                <RadioGroupInput.RadioInput value={ExportLabelTypes.ONE_LABEL} style={{ marginLeft: 20 }}>
                  1面ラベル
                </RadioGroupInput.RadioInput>
                <RadioGroupInput.RadioInput value={ExportLabelTypes.TWO_LABELS}>
                  2面ラベル
                </RadioGroupInput.RadioInput>
                <RadioGroupInput.RadioInput value={ExportLabelTypes.TEN_LABELS}>
                  10面ラベル
                </RadioGroupInput.RadioInput>
              </RadioGroupInput>
              {labelPrintType === ExportLabelTypes.TEN_LABELS && (
              <>
                <div className={mainStyles['d-flex']}>
                  <BaseText bold className={mainStyles['mb-10']}>印字開始番号 </BaseText>
                  {' '}
                  <span className={` ${mainStyles['text-danger']} }`} style={{ marginLeft: '15px' }}>※</span>
                </div>
                <SelectInput
                  value={printStartPosition}
                  options={labelDropdownOptions}
                  onChange={(value) => setPrintStartPosition(value)}
                  style={{ width: '150px' }}
                  menuStyle={{ minWidth: '150px', left: '60%' }}
                />
              </>
              )}
            </div>
            {labelPrintType === ExportLabelTypes.TEN_LABELS && (
            <div>
              <BaseText size="sm" color="var(--danger)">※ 印字開始番号と印刷位置の対応 </BaseText>
              <div className={styles.gridContainer}>
                {Array.from({ length: 10 }, (_, i) => (
                  <div key={i} className={styles.numberBox}>
                    <BaseText>
                      {i + 1}
                    </BaseText>
                  </div>
                ))}
              </div>

            </div>
            )}
          </div>
        </div>
        <div className={styles['modal-body']}>
          <div className={styles['modal-list']}>
            <BaseTable<DocumentWarehousing>
              tableRef={gridRef}
              formName={tableName}
              waitToLoad={loadingAllTags}
              rowData={data}
              columnDefs={colDefModal}
              useSortFromColumnSettingsApi={false}
              noRowsText={noRowText}
              createLogOnDownloadTableData={LogFormName.ExportLabel}
              sideBar
            />
          </div>
          <footer>
            <button type="button" className={[mainStyles.button, mainStyles['btn-gray']].join(' ')} onClick={onClose}>閉じる</button>
            <button type="button" className={[mainStyles.button, mainStyles['btn-primary']].join(' ')} onClick={() => onExportClick()}>実行</button>
          </footer>
        </div>
      </div>
      {(loadingAllTags || exportLabelloading) && <LoadingOverlay />}
    </div>
  );
}

export default ExportLabelComponent;
